import { useState, type FormEvent } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../auth/provider";
import "./login.css";

type Mode = "signIn" | "signUp" | "confirm" | "totp";

export function LoginPage() {
  const { signIn, signUp, confirmSignUp, confirmSignIn, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState<Mode>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // If already authenticated, redirect to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? "/dashboard";
  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (mode === "signIn") {
        const result = await signIn(email, password);
        if (result.nextStep === "TOTP_REQUIRED") {
          setMode("totp");
          setError(null);
        } else {
          navigate(from, { replace: true });
        }
      } else if (mode === "signUp") {
        const result = await signUp(email, password);
        if (result.needsConfirmation) {
          setMode("confirm");
        } else {
          navigate(from, { replace: true });
        }
      } else if (mode === "confirm") {
        await confirmSignUp(email, confirmCode);
        navigate(from, { replace: true });
      } else if (mode === "totp") {
        await confirmSignIn(confirmCode);
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
            Simple <em>Cash Discount</em>
          </Link>
        </div>

        <div className="login-title">
          {mode === "signIn" && "Welcome back"}
          {mode === "signUp" && "Create your account"}
          {mode === "confirm" && "Verify your email"}
          {mode === "totp" && "Verify with authenticator app"}
        </div>
        <div className="login-subtitle">
          {mode === "signIn" && "Sign in to your account"}
          {mode === "signUp" && "Get started with Simple Cash Discount"}
          {mode === "confirm" && `Enter the code sent to ${email}`}
          {mode === "totp" && "Enter the 6-digit code from your authenticator"}
        </div>

        {error && <div className="login-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {mode !== "confirm" && mode !== "totp" && (
            <>
              <div className="login-field">
                <label className="login-label" htmlFor="email">
                  Email
                </label>
                <input
                  className="login-input"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </div>
              <div className="login-field">
                <label className="login-label" htmlFor="password">
                  Password
                </label>
                <input
                  className="login-input"
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete={mode === "signIn" ? "current-password" : "new-password"}
                  placeholder={mode === "signUp" ? "Min 8 characters" : "Your password"}
                  minLength={mode === "signUp" ? 8 : undefined}
                />
              </div>
            </>
          )}

          {(mode === "confirm" || mode === "totp") && (
            <div className="login-field">
              <label className="login-label" htmlFor="code">
                {mode === "totp" ? "Authenticator Code" : "Confirmation Code"}
              </label>
              <input
                className="login-input"
                id="code"
                type="text"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                required
                autoComplete="one-time-code"
                placeholder="123456"
                inputMode="numeric"
              />
            </div>
          )}

          <button className="login-submit" type="submit" disabled={loading}>
            {loading
              ? "Please wait..."
              : mode === "signIn"
                ? "Sign In"
                : mode === "signUp"
                  ? "Create Account"
                  : "Verify"}
          </button>
        </form>

        {mode === "signIn" && (
          <div className="login-toggle">
            Don't have an account?{" "}
            <button onClick={() => { setMode("signUp"); setError(null); }}>
              Sign up
            </button>
          </div>
        )}
        {mode === "signUp" && (
          <div className="login-toggle">
            Already have an account?{" "}
            <button onClick={() => { setMode("signIn"); setError(null); }}>
              Sign in
            </button>
          </div>
        )}
        {mode === "confirm" && (
          <div className="login-toggle">
            <button onClick={() => { setMode("signUp"); setError(null); }}>
              Back to sign up
            </button>
          </div>
        )}
        {mode === "totp" && (
          <div className="login-toggle">
            <button onClick={() => { setMode("signIn"); setError(null); setConfirmCode(""); }}>
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
